import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Loader2, Mail, Phone, MapPin, Linkedin, Calendar, Building2, FileText, Pencil, Check, X as XIcon, ShieldAlert, Archive, Trash2, AlertTriangle } from "lucide-react";
import { getAdminAuthHeaders, useAdminAuth } from "./AdminAuthContext";
import { StatusBadge } from "./StatusBadge";
import { AdminPagination } from "./AdminPagination";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function InfoRow({ icon: Icon, label, value, editing, children }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-100 last:border-0">
      <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-400 mb-0.5">{label}</p>
        {editing ? children : <p className="text-sm text-slate-800 break-words">{value || "—"}</p>}
      </div>
    </div>
  );
}

export default function AdminUsersPage() {
  const { user: adminUser } = useAdminAuth();
  const isSuperAdmin = adminUser?.role === "super_admin";
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [keyword, setKeyword] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [actionLoading, setActionLoading] = useState("");
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [deleteStage, setDeleteStage] = useState(null); // null | "impact" | "confirm"
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const limit = 15;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`, {
        headers: getAdminAuthHeaders(),
        params: { keyword: keyword || undefined, role: roleFilter || undefined, status: statusFilter || undefined, page, limit },
      });
      setUsers(res.data.users);
      setTotal(res.data.total);
    } finally {
      setLoading(false);
    }
  }, [keyword, roleFilter, statusFilter, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [keyword, roleFilter, statusFilter]);

  const toggleStatus = async (u) => {
    setActionLoading(u.user_id);
    try {
      const action = u.status === "active" ? "suspend" : "activate";
      await axios.post(`${API}/admin/users/${u.user_id}/${action}`, {}, { headers: getAdminAuthHeaders() });
      toast.success(action === "activate" ? "Usuario activado" : "Usuario suspendido");
      await load();
      if (detail?.user_id === u.user_id) openDetail(u);
    } finally {
      setActionLoading("");
    }
  };

  const openDetail = async (u) => {
    setEditing(false);
    const res = await axios.get(`${API}/admin/users/${u.user_id}`, { headers: getAdminAuthHeaders() });
    setDetail(res.data);
    setForm({ name: res.data.name || "", phone: res.data.phone || "", location: res.data.location || "", linkedin: res.data.linkedin || "", bio: res.data.bio || "" });
  };

  const startEdit = () => setEditing(true);
  const cancelEdit = () => {
    setEditing(false);
    setForm({ name: detail.name || "", phone: detail.phone || "", location: detail.location || "", linkedin: detail.linkedin || "", bio: detail.bio || "" });
  };
  const saveEdit = async () => {
    try {
      await axios.put(`${API}/admin/users/${detail.user_id}`, form, { headers: getAdminAuthHeaders() });
      setDetail((d) => ({ ...d, ...form }));
      setUsers((prev) => prev.map((u) => (u.user_id === detail.user_id ? { ...u, ...form } : u)));
      setEditing(false);
      toast.success("Datos actualizados");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar el usuario");
    }
  };
  const confirmArchiveAction = async () => {
    try {
      await axios.post(`${API}/admin/users/${detail.user_id}/archive`, {}, { headers: getAdminAuthHeaders() });
      toast.success(`${detail.name} fue archivado`);
      setConfirmArchive(false);
      setDetail(null);
      await load();
    } catch (err) {
      setConfirmArchive(false);
      toast.error(err?.response?.data?.detail || "No se pudo archivar el usuario");
    }
  };

  const openDeleteImpact = async () => {
    setDeleteImpact(null);
    try {
      const res = await axios.get(`${API}/admin/users/${detail.user_id}/deletion-impact`, { headers: getAdminAuthHeaders() });
      setDeleteImpact(res.data);
      setDeleteStage("impact");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo calcular el impacto de la eliminación");
    }
  };

  const proceedToDeleteConfirm = () => {
    setDeleteConfirmText("");
    setDeleteStage("confirm");
  };

  const executeDeletePermanently = async () => {
    setDeleteLoading(true);
    try {
      await axios.post(`${API}/admin/users/${detail.user_id}/delete-permanently`, {}, { headers: getAdminAuthHeaders() });
      toast.success(`${detail.name} fue eliminado definitivamente`);
      setDeleteStage(null);
      setDetail(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar el usuario");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div data-testid="admin-users-page">
      <h1 className="text-xl font-bold text-slate-900 mb-1">Usuarios</h1>
      <p className="text-sm text-slate-500 mb-4">Candidatos y empresas registrados en la plataforma</p>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input data-testid="admin-users-search-input" value={keyword} onChange={(e) => setKeyword(e.target.value)}
            placeholder="Buscar por nombre o email..." className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm" />
        </div>
        <select data-testid="admin-users-role-filter" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Todos los tipos</option>
          <option value="candidate">Candidato</option>
          <option value="employer">Empresa</option>
        </select>
        <select data-testid="admin-users-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="">Todos los estados</option>
          <option value="active">Activo</option>
          <option value="suspended">Suspendido</option>
          <option value="archived">Archivado</option>
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[765px] table-fixed" data-testid="admin-users-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3 w-[130px]">Nombre</th>
              <th className="text-left px-4 py-3 w-[160px]">Email</th>
              <th className="text-left px-4 py-3 w-[70px]">Tipo</th>
              <th className="text-left px-4 py-3 w-[90px]">Estado</th>
              <th className="text-left px-4 py-3 w-[85px]">Registro</th>
              <th className="text-left px-4 py-3 w-[110px]">Empresa</th>
              <th className="text-left px-4 py-3 w-[120px]">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} data-testid={`admin-user-row-${u.user_id}`} className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors">
                <td className="px-4 py-3 font-medium text-slate-900 truncate" title={u.name}>{u.name}</td>
                <td className="px-4 py-3 text-slate-600 truncate" title={u.email}>{u.email}</td>
                <td className="px-4 py-3 text-slate-600 capitalize">{u.role === "candidate" ? "Candidato" : "Empresa"}</td>
                <td className="px-4 py-3"><StatusBadge value={u.status} testId={`admin-user-status-${u.user_id}`} /></td>
                <td className="px-4 py-3 text-slate-500">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-slate-600 truncate" title={u.company_name}>{u.company_name || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-3">
                    <button data-testid={`admin-user-view-${u.user_id}`} onClick={() => openDetail(u)} className="text-xs font-semibold text-[#028CF8] hover:underline">Ver ficha</button>
                    <button
                      data-testid={`admin-user-toggle-status-${u.user_id}`}
                      onClick={() => toggleStatus(u)}
                      disabled={actionLoading === u.user_id}
                      className={`text-xs font-semibold hover:underline ${u.status !== "active" ? "text-emerald-600" : "text-red-600"}`}
                    >
                      {actionLoading === u.user_id ? "..." : u.status !== "active" ? "Activar" : "Suspender"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && users.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-slate-500 text-sm">Sin resultados</td></tr>
            )}
          </tbody>
        </table>
        {loading && <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 text-[#028CF8] animate-spin" /></div>}
      </div>

      <AdminPagination page={page} total={total} limit={limit} onPageChange={setPage} testidPrefix="admin-users" />

      {/* ── Ficha completa — drawer ancho (iPad/desktop) ── */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent
          data-testid="admin-user-detail-sheet"
          className="w-full sm:max-w-[520px] md:max-w-[640px] lg:max-w-[720px] p-0 flex flex-col"
        >
          {detail && (
            <>
              <SheetHeader className="p-6 pb-4 border-b border-slate-100 shrink-0">
                <div className="flex items-start gap-4">
                  <div className="h-14 w-14 rounded-xl bg-[#028CF8]/10 flex items-center justify-center font-poppins font-bold text-[#028CF8] text-xl shrink-0">
                    {detail.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <SheetTitle className="font-poppins text-xl truncate" data-testid="admin-user-detail-name">{detail.name}</SheetTitle>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                        {detail.role === "candidate" ? "Candidato" : "Empresa"}
                      </span>
                      <StatusBadge value={detail.status} testId="admin-user-detail-status" />
                    </div>
                  </div>
                </div>
              </SheetHeader>

              <div className="flex-1 overflow-y-auto p-6">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600">Información personal</h3>
                  {!editing ? (
                    <button data-testid="admin-user-edit-toggle" onClick={startEdit} className="flex items-center gap-1.5 text-xs font-semibold text-[#028CF8] hover:underline">
                      <Pencil className="h-3.5 w-3.5" /> Editar
                    </button>
                  ) : (
                    <div className="flex items-center gap-3">
                      <button data-testid="admin-user-edit-cancel" onClick={cancelEdit} className="flex items-center gap-1 text-xs font-semibold text-slate-500 hover:underline">
                        <XIcon className="h-3.5 w-3.5" /> Cancelar
                      </button>
                      <button data-testid="admin-user-edit-save" onClick={saveEdit} className="flex items-center gap-1 text-xs font-semibold text-emerald-600 hover:underline">
                        <Check className="h-3.5 w-3.5" /> Guardar
                      </button>
                    </div>
                  )}
                </div>

                <InfoRow icon={Mail} label="Email" value={detail.email} />
                <InfoRow icon={Phone} label="Teléfono" value={detail.phone} editing={editing}>
                  <Input data-testid="admin-user-edit-phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="h-8 text-sm" />
                </InfoRow>
                <InfoRow icon={MapPin} label="Ubicación" value={detail.location} editing={editing}>
                  <Input data-testid="admin-user-edit-location" value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} className="h-8 text-sm" />
                </InfoRow>
                <InfoRow icon={Linkedin} label="LinkedIn" value={detail.linkedin} editing={editing}>
                  <Input data-testid="admin-user-edit-linkedin" value={form.linkedin} onChange={(e) => setForm((f) => ({ ...f, linkedin: e.target.value }))} className="h-8 text-sm" />
                </InfoRow>
                <InfoRow icon={FileText} label="Bio" value={detail.bio} editing={editing}>
                  <Textarea data-testid="admin-user-edit-bio" value={form.bio} onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))} className="text-sm min-h-[70px]" />
                </InfoRow>
                <InfoRow icon={Calendar} label="Fecha de registro" value={new Date(detail.created_at).toLocaleString()} />

                {detail.role === "employer" && (
                  <>
                    <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600 mt-6 mb-1">Empresa asociada</h3>
                    <InfoRow icon={Building2} label="Empresa" value={detail.company ? `${detail.company.name} · ${detail.jobs_posted || 0} empleos publicados` : "Sin empresa asociada"} />
                  </>
                )}
                {detail.role === "candidate" && (
                  <>
                    <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600 mt-6 mb-1">Actividad</h3>
                    <InfoRow icon={FileText} label="Aplicaciones enviadas" value={detail.applications_count} />
                  </>
                )}
              </div>

              <SheetFooter className="p-4 border-t border-slate-100 shrink-0 gap-2 flex-wrap">
                <Button
                  data-testid="admin-user-detail-toggle-status"
                  variant="outline"
                  disabled={actionLoading === detail.user_id}
                  onClick={() => toggleStatus(detail)}
                  className={detail.status !== "active" ? "text-emerald-600 border-emerald-200" : "text-amber-600 border-amber-200"}
                >
                  <ShieldAlert className="h-4 w-4 mr-1.5" />
                  {detail.status !== "active" ? "Activar cuenta" : "Suspender cuenta"}
                </Button>
                {detail.status !== "archived" && (
                  <Button data-testid="admin-user-detail-archive" variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50 hover:text-amber-700" onClick={() => setConfirmArchive(true)}>
                    <Archive className="h-4 w-4 mr-1.5" /> Archivar
                  </Button>
                )}
                {isSuperAdmin && (
                  <Button data-testid="admin-user-detail-delete" variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={openDeleteImpact}>
                    <Trash2 className="h-4 w-4 mr-1.5" /> Eliminar definitivamente
                  </Button>
                )}
              </SheetFooter>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* ── Confirmación de acción destructiva ── */}
      <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <AlertDialogContent data-testid="admin-user-archive-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Archivar a {detail?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              La cuenta se ocultará de la app pública y no podrá iniciar sesión.
              {detail?.role === "candidate" && " Sus aplicaciones y su historial se preservan (no se borran)."}
              {detail?.role === "employer" && detail?.company && ` Esta acción está bloqueada mientras tenga una empresa activa (${detail.company.name}, ${detail.jobs_posted || 0} empleos) — primero archivá o transferí la empresa.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-user-archive-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-user-archive-confirm-button" onClick={confirmArchiveAction} className="bg-amber-600 hover:bg-amber-700">
              Archivar cuenta
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Eliminar definitivamente: paso 1, impacto ── */}
      <AlertDialog open={deleteStage === "impact"} onOpenChange={(o) => { if (!o) { setDeleteStage(null); setDeleteImpact(null); } }}>
        <AlertDialogContent data-testid="admin-user-delete-impact-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" /> Eliminar a {detail?.name} definitivamente
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-left text-sm text-slate-600">
                {deleteImpact?.role === "candidate" && (
                  <>
                    <p>Se van a borrar de forma irreversible:</p>
                    <ul className="list-disc pl-5 space-y-0.5" data-testid="admin-user-delete-impact-candidate-list">
                      <li>Cuenta y perfil personal</li>
                      <li>{deleteImpact.has_primary_cv ? "Su CV principal" : "Sin CV principal"} y {deleteImpact.cv_versions_count} versión(es) de CV adaptado</li>
                      <li>{deleteImpact.saved_jobs_count} empleo(s) guardado(s) y {deleteImpact.saved_searches_count} búsqueda(s) guardada(s)</li>
                    </ul>
                    <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                      Sus {deleteImpact.applications_count} aplicación(es) NO se borran — quedan como historial para las empresas, pero completamente anonimizadas (sin nombre, email, teléfono, CV ni ningún otro dato que lo identifique).
                    </p>
                  </>
                )}
                {deleteImpact?.role === "employer" && (
                  deleteImpact.can_delete ? (
                    <p data-testid="admin-user-delete-impact-employer-ok">Este usuario no tiene relaciones con empresas — se puede eliminar de forma segura.</p>
                  ) : (
                    <div className="space-y-1.5" data-testid="admin-user-delete-impact-blocked">
                      {deleteImpact.blocking_reasons.map((r, i) => (
                        <p key={i} className="text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5">{r}</p>
                      ))}
                    </div>
                  )
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-user-delete-impact-cancel">Cancelar</AlertDialogCancel>
            {deleteImpact?.can_delete && (
              <Button data-testid="admin-user-delete-impact-continue" onClick={proceedToDeleteConfirm} className="bg-red-600 hover:bg-red-700 text-white">
                Continuar
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Eliminar definitivamente: paso 2, confirmación fuerte ── */}
      <AlertDialog open={deleteStage === "confirm"} onOpenChange={(o) => !o && setDeleteStage("impact")}>
        <AlertDialogContent data-testid="admin-user-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Esta acción es irreversible</AlertDialogTitle>
            <AlertDialogDescription>
              Escribí <strong>ELIMINAR</strong> para confirmar que querés borrar definitivamente a {detail?.name}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            data-testid="admin-user-delete-confirm-input"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="ELIMINAR"
            className="mb-2"
          />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-user-delete-confirm-cancel">Volver</AlertDialogCancel>
            <Button
              data-testid="admin-user-delete-confirm-button"
              disabled={deleteConfirmText !== "ELIMINAR" || deleteLoading}
              onClick={executeDeletePermanently}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            >
              {deleteLoading ? "Eliminando..." : "Eliminar definitivamente"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
